import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Clock,
  CheckCircle2,
  Search,
  FileSearch,
  X,
  Activity,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/auth";
import {
  loadPrograms,
  saveSubKegiatanAPI,
  flattenSubs,
  updateSubKegiatan,
} from "../utils/programStorage";
import {
  loadAssignments,
  getAssignment,
  subsAssignedToUser,
} from "../utils/assignmentStorage";
import {
  computeHighlight,
  newHistoryEntry,
  appendHistory,
  summarizeHighlights,
} from "../utils/progressUtils";
import SubListCard from "../components/Progress/SubListCard";
import ProgressDetailPanel from "../components/Progress/ProgressDetailPanel";
import LaporanProgresPanel from "../components/Progress/LaporanProgresPanel";
import { Toaster, toast } from "../components/ui/sonner";

function StatCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3"
    >
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${
          accent || "bg-[#e8edf2] text-[#1e3a5f]"
        }`}
      >
        <Icon className="h-4 w-4" strokeWidth={2} />
      </div>
      <div className="min-w-0 leading-tight">
        <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
          {label}
        </div>
        <div className="mt-0.5 font-display text-xl font-bold text-slate-900">{value}</div>
      </div>
    </div>
  );
}

export default function Progress() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get("focus");
  const [programs, setPrograms] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [loadingData, setLoadingData] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [programFilter, setProgramFilter] = useState("ALL");
  const [highlightFilter, setHighlightFilter] = useState("ALL");
  const [onlyMine, setOnlyMine] = useState(
    user?.role === "ANGGOTA_TIM" || user?.role === "KETUA_TIM",
  );

  useEffect(() => {
    let mounted = true;
    Promise.all([loadPrograms(), loadAssignments()])
      .then(([prog, assign]) => {
        if (mounted) {
          setPrograms(prog);
          setAssignments(assign);
          setLoadingData(false);
        }
      })
      .catch(() => {
        if (mounted) setLoadingData(false);
      });

    api
      .get("/users")
      .then((res) => { if (mounted) setUsers(res.data); })
      .catch(() => {});

    return () => { mounted = false; };
  }, []);

  const userById = useMemo(() => {
    const m = {};
    users.forEach((u) => (m[u.id] = u));
    return m;
  }, [users]);

  // All eligible subs (exclude P11c)
  const allSubs = useMemo(
    () => flattenSubs(programs).filter((s) => s.specialType !== "AUDIT_INVESTIGASI"),
    [programs],
  );

  const mineSubs = useMemo(
    () => (user ? subsAssignedToUser(assignments, user.id, allSubs) : []),
    [assignments, allSubs, user],
  );

  // Merge assignmentRole info into a map for easy lookup
  const assignmentRoleById = useMemo(() => {
    const m = {};
    mineSubs.forEach((s) => {
      m[s.id] = s.assignmentRole;
    });
    return m;
  }, [mineSubs]);

  // Visible subs after all filters
  const visible = useMemo(() => {
    const base = onlyMine ? mineSubs : allSubs;
    const q = search.trim().toLowerCase();
    return base.filter((s) => {
      if (programFilter !== "ALL" && s.programId !== programFilter) return false;
      if (q && !s.nama.toLowerCase().includes(q)) return false;
      if (highlightFilter !== "ALL") {
        const h = computeHighlight(s);
        if (highlightFilter === "OVERDUE" && h.level !== "RED") return false;
        if (highlightFilter === "SOON" && h.level !== "YELLOW") return false;
        if (highlightFilter === "DONE" && h.level !== "GREEN" && h.level !== "DONE_LATE")
          return false;
      }
      return true;
    });
  }, [onlyMine, mineSubs, allSubs, search, programFilter, highlightFilter]);

  const summary = useMemo(() => summarizeHighlights(visible), [visible]);

  // Selected sub — fresh from programs (to include latest edits)
  const selected = useMemo(() => {
    if (!selectedId) return null;
    const s = flattenSubs(programs).find((x) => x.id === selectedId);
    return s || null;
  }, [selectedId, programs]);

  // Default selection: focus from URL param first, then first visible sub
  useEffect(() => {
    if (focusId && !selectedId) {
      const exists = flattenSubs(programs).some((s) => s.id === focusId);
      if (exists) {
        setSelectedId(focusId);
        return;
      }
    }
    if (!selectedId && visible.length > 0) setSelectedId(visible[0].id);
    if (selectedId && !visible.find((s) => s.id === selectedId) && visible.length > 0) {
      setSelectedId(visible[0].id);
    }
    if (visible.length === 0) setSelectedId(null);
  }, [visible, selectedId, focusId, programs]);

  // ---------- Mutations ----------
  const updateAndPersist = async (subId, patch) => {
    const next = updateSubKegiatan(programs, subId, patch);
    setPrograms(next);
    try {
      await saveSubKegiatanAPI(subId, {
        status: patch.status,
        progress: patch.progress,
        catatan: patch.catatan,
        targetMulai: patch.targetMulai,
        targetSelesai: patch.targetSelesai,
        prioritas: patch.prioritas,
      });
    } catch (err) {
      toast.error("Gagal menyimpan ke server: " + (err?.response?.data?.detail || err.message));
    }
  };

  const handleInputProgress = async (subId, values, actor) => {
    const current = flattenSubs(programs).find((s) => s.id === subId);
    if (!current) return;

    const wasSelesai = current.status === "SELESAI";
    const nowSelesai = values.status === "SELESAI";
    const action = !wasSelesai && nowSelesai ? "Tandai selesai" : "Input progres";

    const entry = newHistoryEntry({
      user: actor,
      action,
      catatan:
        `Progres: ${values.progress}%, Status: ${values.status}` +
        (values.namaDokumen ? `\nDokumen: ${values.namaDokumen}` : "") +
        (values.catatan ? `\n${values.catatan}` : ""),
    });

    const selesaiAktual =
      nowSelesai && !values.selesaiAktual
        ? new Date().toISOString().slice(0, 10)
        : values.selesaiAktual;

    const prevHistory = Array.isArray(current.history) ? current.history : [];

    await updateAndPersist(subId, {
      progress: values.progress,
      status: values.status,
      catatan: values.catatan,
      namaDokumen: values.namaDokumen,
      kma: values.kma,
      mulaiAktual: values.mulaiAktual,
      selesaiAktual,
      history: [entry, ...prevHistory],
    });

    toast.success("Progres berhasil disimpan");
  };

  const handleVerify = async (subId, checked, actor) => {
    const current = flattenSubs(programs).find((s) => s.id === subId);
    if (!current) return;
    const prevHistory = Array.isArray(current.history) ? current.history : [];
    const entry = newHistoryEntry({
      user: actor,
      action: "Verifikasi Ketua Tim",
      catatan: checked ? "Diverifikasi oleh Ketua Tim." : "Verifikasi dibatalkan.",
    });
    await updateAndPersist(subId, {
      verifiedByKetua: checked,
      verifiedAt: checked ? new Date().toISOString() : null,
      verifiedBy: checked ? actor.id : null,
      history: [entry, ...prevHistory],
    });
    toast.success(checked ? "Verifikasi berhasil" : "Verifikasi dibatalkan");
  };

  const handleAddKoordinasi = async (subId, text, actor) => {
    const current = flattenSubs(programs).find((s) => s.id === subId);
    if (!current) return;
    const prevHistory = Array.isArray(current.history) ? current.history : [];
    const entry = newHistoryEntry({
      user: actor,
      action: "Catatan koordinasi",
      catatan: text,
    });
    await updateAndPersist(subId, { history: [entry, ...prevHistory] });
    toast.success("Catatan koordinasi ditambahkan");
  };

  const resetFilters = () => {
    setSearch("");
    setProgramFilter("ALL");
    setHighlightFilter("ALL");
  };

  const hasActiveFilters =
    search || programFilter !== "ALL" || highlightFilter !== "ALL";

  const activeAssignment = selected ? getAssignment(assignments, selected.id) : null;
  const assignmentRole = selected ? assignmentRoleById[selected.id] : null;

  if (loadingData) {
    return (
      <div className="flex items-center justify-center py-20 text-[13px] text-slate-500">
        Memuat data…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="progress-page">
      <Toaster position="top-right" richColors />

      <div
        className="rounded-2xl px-6 py-5 text-white"
        style={{ background: "linear-gradient(135deg, #4338ca 0%, #6366f1 55%, #818cf8 100%)" }}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/20">
              <Activity className="h-5 w-5" strokeWidth={2} />
            </div>
            <div>
              <h1 className="font-display text-lg font-bold leading-tight">Progress Kegiatan</h1>
              <p className="mt-0.5 text-[11.5px] text-indigo-200">
                Input dan pantau realisasi sub-kegiatan · verifikasi Ketua Tim · koordinasi Dalnis
              </p>
            </div>
          </div>
          <span className="rounded-full bg-white/20 px-2.5 py-1 text-[11px] font-semibold">
            {visible.length} dari {allSubs.length} kegiatan
          </span>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          icon={AlertTriangle}
          label="Lewat Deadline"
          value={summary.RED}
          accent="bg-red-50 text-red-700"
          testId="summary-red"
        />
        <StatCard
          icon={Clock}
          label="Deadline Dekat"
          value={summary.YELLOW}
          accent="bg-amber-50 text-amber-700"
          testId="summary-yellow"
        />
        <StatCard
          icon={CheckCircle2}
          label="Selesai Tepat Waktu"
          value={summary.GREEN}
          accent="bg-emerald-50 text-emerald-700"
          testId="summary-green"
        />
        <StatCard
          icon={FileSearch}
          label="Total Terlihat"
          value={visible.length}
          testId="summary-total-visible"
        />
      </div>

      {/* Laporan resmi — Word, PDF, dan kirim WA ke Irban */}
      <LaporanProgresPanel subs={allSubs} user={user} />

      {/* Filters */}
      <div
        data-testid="progress-filter-bar"
        className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 md:flex-row md:items-center"
      >
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari nama sub-kegiatan…"
            data-testid="progress-search"
            className="h-9 w-full rounded-md border border-gray-300 bg-white pl-8 pr-3 text-[13px] text-slate-900 placeholder:text-slate-400 focus:border-[#1e3a5f] focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
          />
        </div>
        <select
          value={programFilter}
          onChange={(e) => setProgramFilter(e.target.value)}
          data-testid="progress-filter-program"
          className="h-9 rounded-md border border-gray-300 bg-white px-2.5 text-[13px] text-slate-800"
        >
          <option value="ALL">Semua Program</option>
          {programs.map((p) => {
            const shortName = p.nama.length > 40 ? `${p.nama.slice(0, 40)}…` : p.nama;
            const label = `${p.kode} — ${shortName}`;
            return (
              <option key={p.id} value={p.id}>
                {label}
              </option>
            );
          })}
        </select>
        <select
          value={highlightFilter}
          onChange={(e) => setHighlightFilter(e.target.value)}
          data-testid="progress-filter-highlight"
          className="h-9 rounded-md border border-gray-300 bg-white px-2.5 text-[13px] text-slate-800"
        >
          <option value="ALL">Semua Kondisi</option>
          <option value="OVERDUE">Lewat deadline</option>
          <option value="SOON">Deadline dekat</option>
          <option value="DONE">Sudah selesai</option>
        </select>
        <label className="inline-flex cursor-pointer items-center gap-2 text-[12px] font-semibold text-slate-700">
          <input
            type="checkbox"
            checked={onlyMine}
            onChange={(e) => setOnlyMine(e.target.checked)}
            data-testid="progress-filter-mine"
            className="h-4 w-4 rounded border-gray-300 accent-[#1e3a5f]"
          />
          Tugas saya
        </label>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={resetFilters}
            data-testid="progress-filter-reset"
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-[12px] font-semibold text-slate-700 hover:border-[#1e3a5f] hover:text-[#1e3a5f]"
          >
            <X className="h-3.5 w-3.5" />
            Reset
          </button>
        ) : null}
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,380px)_1fr]">
        {/* List */}
        <div className="space-y-2">
          <div className="flex items-center justify-between px-1">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              {onlyMine ? "Tugas saya" : "Semua sub-kegiatan"}
            </div>
            <div className="text-[11px] text-slate-400">{visible.length} item</div>
          </div>
          <div className="max-h-[70vh] space-y-2 overflow-y-auto pr-1" data-testid="progress-sub-list">
            {visible.length === 0 ? (
              <div className="rounded-md border border-dashed border-gray-300 bg-white px-4 py-10 text-center text-[12px] text-slate-500">
                Tidak ada sub-kegiatan yang cocok.
              </div>
            ) : (
              visible.map((s) => (
                <SubListCard
                  key={s.id}
                  sub={s}
                  selected={s.id === selectedId}
                  onSelect={(x) => setSelectedId(x.id)}
                  assignmentRole={assignmentRoleById[s.id]}
                />
              ))
            )}
          </div>
        </div>

        {/* Detail */}
        <div>
          {selected ? (
            <ProgressDetailPanel
              sub={selected}
              user={user}
              assignment={activeAssignment}
              userById={userById}
              onInputProgress={handleInputProgress}
              onVerify={handleVerify}
              onAddKoordinasi={handleAddKoordinasi}
              key={selected.id}
            />
          ) : (
            <div
              data-testid="progress-empty"
              className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white px-6 py-20 text-center"
            >
              <FileSearch className="h-6 w-6 text-slate-400" strokeWidth={1.8} />
              <h3 className="mt-3 font-display text-base font-semibold text-slate-900">
                Pilih sub-kegiatan
              </h3>
              <p className="mt-1 max-w-md text-[12px] text-slate-500">
                Pilih sub-kegiatan dari daftar di kiri untuk melihat detail dan input progres.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
